import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import GarmentAllocator from '@/components/GarmentAllocator';
import BulkAddSheet from '@/components/BulkAddSheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TagCombobox } from '@/components/TagCombobox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { uid, money, PAYMENT_METHODS } from '@/lib/helpers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/context/AppContext';
import { Loader2, Check, ChevronRight, Table2 } from 'lucide-react';

export default function PurchaseWizard() {
  const navigate = useNavigate();
  const { stores } = useApp();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [category, setCategory] = useState('');
  const [pendingName, setPendingName] = useState(null);
  const [invoice, setInvoice] = useState('');
  const [amount, setAmount] = useState('');
  const amountTouched = useRef(false); // user manually edited the purchase amount
  const [payStatus, setPayStatus] = useState('UNPAID');
  const [paid, setPaid] = useState('');
  const [payMethod, setPayMethod] = useState('CASH');
  const [notes, setNotes] = useState('');
  const [garments, setGarments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Merge bulk-added garments into the purchase list, skipping duplicate barcodes.
  const addBulkGarments = (gs) => {
    setGarments((prev) => {
      const seen = new Set(prev.map((g) => g.barcode));
      const merged = [...prev];
      for (const g of gs) { if (!seen.has(g.barcode)) { merged.push(g); seen.add(g.barcode); } }
      return merged;
    });
  };

  const { data: supData } = useQuery({ queryKey: ['suppliers'], queryFn: async () => (await api.get('/suppliers')).data.data });
  React.useEffect(() => { if (supData) setSuppliers(supData); }, [supData]);
  const { data: tagData } = useQuery({ queryKey: ['catalog-tags'], queryFn: async () => (await api.get('/catalog/tags')).data.data });
  const tags = tagData || [];
  const selectedSup = suppliers.find((s) => s.id === supplierId);
  // A supplier's usual categories (most-purchased first) — shown as quick chips.
  const { data: supCats = [] } = useQuery({
    queryKey: ['supplier-categories', supplierId],
    queryFn: async () => (await api.get(`/suppliers/${supplierId}/categories`)).data.data,
    enabled: !!supplierId,
  });

  const paidAmt = payStatus === 'PAID' ? Number(amount || 0) : (payStatus === 'PARTIAL' ? Number(paid || 0) : 0);
  const outstanding = Math.max(0, Number(amount || 0) - paidAmt);

  // Auto-total the purchase amount from each item's purchase (cost) price. Stays
  // editable — once the user types their own figure we stop overwriting it.
  const computedAmount = React.useMemo(
    () => Math.round(garments.reduce((s, g) => s + (Number(g.cost) || 0), 0) * 100) / 100,
    [garments],
  );
  React.useEffect(() => {
    if (!amountTouched.current && computedAmount > 0) setAmount(String(computedAmount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedAmount]);

  // Select an existing name; if it isn't a supplier yet (category-only), ask (3B).
  const pickSupplier = (name) => {
    const tag = tags.find((t) => t.name.toLowerCase() === name.toLowerCase());
    setSupplierName(name);
    if (tag && tag.supplier_id) { setSupplierId(tag.supplier_id); }
    else { setSupplierId(''); setPendingName(name); }
  };
  // Brand-new name created via the combobox already exists as a supplier.
  const onSupplierCreated = (res) => {
    setSupplierName(res.name); setSupplierId(res.supplier_id || '');
    qc.invalidateQueries({ queryKey: ['suppliers'] });
  };
  const confirmMakeSupplier = async () => {
    const name = pendingName; if (!name) return;
    try {
      const r = await api.post('/catalog/tags', { name, ensure_supplier: true });
      setSupplierId(r.data.data.supplier_id || ''); setSupplierName(r.data.data.name);
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      qc.invalidateQueries({ queryKey: ['catalog-tags'] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPendingName(null); }
  };
  const cancelMakeSupplier = () => { setPendingName(null); setSupplierName(''); setSupplierId(''); };

  const confirm = async () => {
    if (!supplierId) { toast.error('Select a supplier.'); return; }
    if (!garments.length) { toast.error('Add garments received.'); return; }
    if (garments.some((g) => !g.store_id)) { toast.error('Allocate every garment to a store.'); return; }
    setSaving(true);
    try {
      const r = await api.post('/purchases', {
        supplier_id: supplierId, supplier_invoice: invoice, amount: Number(amount || 0),
        paid: paidAmt, payment_method: payMethod, garments, notes, client_txn_id: uid(),
      });
      toast.success(`Purchase ${r.data.data.purchase_no} created · ${r.data.data.garments_created} garments added`);
      navigate('/purchases');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Page>
      <TopBar title="+ Purchase" back showStore={false} />
      <div className="px-4 pt-3 pb-32">
        <Steps step={step} onJump={setStep} />

        {step === 1 && (
          <div className="mt-4 space-y-4">
            <div className="rounded-2xl border border-border bg-card p-4 space-y-4">
              <div>
                <Label className="text-xs">Supplier <span className="text-destructive">*</span></Label>
                <div className="mt-1">
                  <TagCombobox value={supplierName} onChange={pickSupplier} onCreated={onSupplierCreated} placeholder="Select or create supplier" testId="purchase-supplier" />
                </div>
                {selectedSup && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-secondary px-2 py-0.5">Total: {money(selectedSup.total_purchases)}</span>
                    <span className="rounded-full bg-rose-50 px-2 py-0.5 text-rose-700">Outstanding: {money(selectedSup.outstanding)}</span>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Category <span className="text-muted-foreground">(optional)</span></Label>
                <div className="mt-1">
                  <TagCombobox value={category} onChange={setCategory} placeholder="Select or create category"
                    testId="purchase-category" filterKind="category" createExtra={{ ensure_supplier: false }} />
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground">The same category (e.g. &ldquo;Pakistani&rdquo;) can come from many suppliers. Pick it here to pre-fill every item — still editable per item.</p>
                {supCats.length > 0 && (
                  <div className="mt-2" data-testid="purchase-supplier-cats">
                    <p className="mb-1 text-[11px] font-medium text-muted-foreground">Usual for {supplierName || 'this supplier'} — tap to use:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {supCats.map((c) => (
                        <button key={c.category} type="button" onClick={() => setCategory(c.category)}
                          data-testid={`purchase-supcat-${c.category}`}
                          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${category === c.category ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground hover:bg-secondary'}`}>
                          {c.category}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {category && (
                  <button type="button" onClick={() => setCategory('')} className="mt-1 text-[11px] font-medium text-primary" data-testid="purchase-category-clear">Clear category</button>
                )}
              </div>
            </div>
            <Button data-testid="purchase-next-1" className="h-12 w-full rounded-xl" disabled={!supplierId} onClick={() => setStep(2)}>Next <ChevronRight className="ml-1 h-4 w-4" /></Button>
          </div>
        )}

        <AlertDialog open={!!pendingName} onOpenChange={(o) => { if (!o) cancelMakeSupplier(); }}>
          <AlertDialogContent data-testid="purchase-make-supplier-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Create supplier?</AlertDialogTitle>
              <AlertDialogDescription>&ldquo;{pendingName}&rdquo; is currently only a category. Create it as a supplier so this purchase can be linked to it?</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={cancelMakeSupplier} data-testid="purchase-make-supplier-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmMakeSupplier} data-testid="purchase-make-supplier-ok">Create supplier</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {step === 2 && (
          <div className="mt-4 space-y-3">
            <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
              <F label="Supplier invoice no."><Input value={invoice} onChange={(e) => setInvoice(e.target.value)} className="h-11 rounded-xl" data-testid="purchase-invoice" placeholder="e.g. KT-4587" /></F>
              <F label="Purchase amount (₹)">
                <Input inputMode="decimal" value={amount} onChange={(e) => { amountTouched.current = true; setAmount(e.target.value); }} className="h-11 rounded-xl" data-testid="purchase-amount" placeholder="Auto-totals from item purchase prices" />
                {computedAmount > 0 ? (
                  <p className="mt-1 text-[11px] text-muted-foreground" data-testid="purchase-amount-auto">
                    Auto-totalled from item purchase prices: <b>{money(computedAmount)}</b>
                    {Number(amount || 0) !== computedAmount && (
                      <> · <button type="button" className="font-medium text-primary" data-testid="purchase-amount-reset" onClick={() => { amountTouched.current = false; setAmount(String(computedAmount)); }}>reset to auto</button></>
                    )}
                  </p>
                ) : (
                  <p className="mt-1 text-[11px] text-muted-foreground">Leave blank — we&rsquo;ll total this from the item purchase prices you add next (still editable for freight, rounding, etc.).</p>
                )}
              </F>
              <F label="Payment status">
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {['PAID', 'PARTIAL', 'UNPAID'].map((s) => (
                    <button key={s} data-testid={`purchase-paystatus-${s}`} onClick={() => setPayStatus(s)}
                      className={`h-10 rounded-xl text-sm font-medium ${payStatus === s ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>{s}</button>
                  ))}
                </div>
              </F>
              {payStatus === 'PARTIAL' && (
                <F label="Amount paid (₹)"><Input inputMode="numeric" value={paid} onChange={(e) => setPaid(e.target.value)} className="h-11 rounded-xl" data-testid="purchase-paid" /></F>
              )}
              {payStatus !== 'UNPAID' && (
                <F label="Payment method">
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="purchase-paymethod"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.filter((m) => m !== 'CREDIT').map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
                  </Select>
                </F>
              )}
              <F label="Notes"><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-11 rounded-xl" /></F>
              <div className="flex justify-between rounded-xl bg-secondary/60 p-3 text-sm">
                <span>Outstanding</span><span className="font-mono font-semibold">{money(outstanding)}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" className="h-12 flex-1 rounded-xl" onClick={() => setStep(1)}>Back</Button>
              <Button data-testid="purchase-next-2" className="h-12 flex-1 rounded-xl" onClick={() => setStep(3)}>Next</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-muted-foreground">Add or import the garments received, then allocate to stores.</p>
            <Button variant="secondary" className="h-11 w-full rounded-xl" onClick={() => setBulkOpen(true)} data-testid="purchase-bulk-add">
              <Table2 className="mr-2 h-4 w-4" /> Bulk Add Items
            </Button>
            <GarmentAllocator garments={garments} setGarments={setGarments} defaultCategory={category} />
            <div className="flex gap-2">
              <Button variant="secondary" className="h-12 flex-1 rounded-xl" onClick={() => setStep(2)}>Back</Button>
              <Button data-testid="purchase-next-3" className="h-12 flex-1 rounded-xl" disabled={!garments.length} onClick={() => setStep(4)}>Review</Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mt-4 space-y-3">
            <div className="rounded-2xl border border-border bg-card p-4">
              <h3 className="font-display text-lg font-semibold">Confirm Purchase</h3>
              <div className="mt-3 space-y-1.5 text-sm">
                <SummaryRow label="Supplier" value={selectedSup?.name} />
                <SummaryRow label="Invoice" value={invoice || '—'} />
                <SummaryRow label="Purchase value" value={money(amount)} />
                <SummaryRow label="Paid" value={money(paidAmt)} />
                <SummaryRow label="Outstanding" value={money(outstanding)} />
                <SummaryRow label="Garments received" value={garments.length} />
                {stores.map((s) => (
                  <SummaryRow key={s.id} label={s.name} value={garments.filter((g) => g.store_id === s.id).length} />
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" className="h-12 flex-1 rounded-xl" onClick={() => setStep(3)}>Back</Button>
              <Button data-testid="purchase-confirm" className="h-12 flex-1 rounded-xl font-semibold" disabled={saving} onClick={confirm}>
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="mr-1 h-5 w-5" />Confirm &amp; Add Stock</>}
              </Button>
            </div>
          </div>
        )}
      </div>
      <BulkAddSheet
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        mode="purchase"
        defaultCategory={category}
        invoiceNo={invoice}
        existingBarcodes={garments.map((g) => g.barcode)}
        onAddGarments={addBulkGarments}
      />
    </Page>
  );
}

const Steps = ({ step, onJump }) => {
  const labels = ['Supplier', 'Details', 'Garments', 'Confirm'];
  return (
    <div className="flex items-center gap-1">
      {labels.map((l, i) => {
        const n = i + 1;
        const done = step >= n;
        const clickable = n <= step; // jump back to any earlier/visited step to check or edit
        return (
          <React.Fragment key={l}>
            <button type="button" disabled={!clickable} onClick={() => clickable && onJump?.(n)}
              data-testid={`purchase-step-${n}`}
              className={`flex items-center gap-1.5 rounded-lg px-1 py-0.5 transition-opacity ${done ? 'text-primary' : 'text-muted-foreground'} ${clickable ? 'cursor-pointer hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40' : 'cursor-default'}`}
              title={clickable && n < step ? `Go back to ${l}` : l}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${done ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>{n}</span>
              <span className="hidden text-xs font-medium sm:inline">{l}</span>
            </button>
            {i < labels.length - 1 && <div className={`h-px flex-1 ${step > n ? 'bg-primary' : 'bg-border'}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const F = ({ label, children }) => (<div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>);
const SummaryRow = ({ label, value }) => (<div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}</span></div>);
